import { useState } from 'react';
import type { UseDragDropProps, UseDragDropReturn } from '../types';

/**
 * Custom hook for managing drag and drop state and handlers
 *
 * Provides drag-and-drop functionality for file uploads with:
 * - Visual feedback during drag operations
 * - Event handling for drag over, leave, and drop
 * - Prevention of default browser behavior
 *
 * @param props - Hook configuration
 * @returns Drag-drop state and event handlers
 */
export const useDragDrop = ({
  onFilesDropped,
  disabled = false,
}: UseDragDropProps): UseDragDropReturn => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!disabled) {
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFilesDropped(files);
      }
    }
  };

  return {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
